import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Grid3X3, List, FileText, Zap, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import ProductSpecimen from '../components/assay/ProductSpecimen';
import { parseQuickOrder } from '../lib/shopping';
import { catalogAccess } from '../lib/catalogAccess';
import { sortCatalogProducts } from '../lib/catalogOrder';

// Parse "R320 x10, C1295 x5" or line-separated "R320 x10" entries.

// One kit is ten vials, so the unit a price refers to is stated rather than assumed.
function unitLabel(specification) {
  return /x\s*10\s*vials/i.test(specification || '') ? 'Per 10-vial kit' : 'Per listed unit';
}

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const gridItem = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } },
};

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const search = params.get('search') || '';
  const category = params.get('category') || '';
  const viewMode = params.get('view') === 'list' ? 'list' : 'grid';
  const changeFilter = (key, value) => setParams(previous => { const next = new URLSearchParams(previous); value ? next.set(key, value) : next.delete(key); return next; }, { replace: true });
  const setSearch = value => changeFilter('search', value);
  const setCategory = value => changeFilter('category', value);
  const setViewMode = value => changeFilter('view', value);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickText, setQuickText] = useState('');
  const [quickBusy, setQuickBusy] = useState(false);
  const quickLock = useRef(false);
  const [quickResult, setQuickResult] = useState('');
  const requestRef = useRef(null);
  const { addItem } = useCart();
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const access = catalogAccess(user);
  const isGuest = access.status === 'guest';

  // Debounce search so every keystroke does not fire an API call.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchProducts = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const res = await api.get('/products', { params: { search: debouncedSearch, category, limit: 500 }, signal: controller.signal, timeout: 15000 });
      if (controller.signal.aborted) return;
      setProducts(sortCatalogProducts(res.data.products));
      setTotal(res.data.total);
      setCategories(res.data.categories || []);
      setFetchFailed(false);
    } catch (err) {
      if (controller.signal.aborted) return;
      setFetchFailed(true);
      toast.error('Failed to load products');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [debouncedSearch, category]);

  useEffect(() => { fetchProducts(); return () => requestRef.current?.abort(); }, [fetchProducts]);

  const handleAddToCart = (product) => {
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  const handleQuickAdd = async () => {
    if (quickLock.current) return;
    const entries = parseQuickOrder(quickText);
    if (entries.length === 0) {
      toast.error('Enter SKUs like: R320 x10, C1295 x5');
      return;
    }
    quickLock.current = true;
    setQuickBusy(true);
    try {
      // Match against the loaded catalog first; fetch anything missing by SKU.
      const bySku = new Map(products.map(p => [(p.sku || '').toUpperCase(), p]));
      const missing = entries.filter(e => !e.error && !bySku.has(e.sku));
      const lookupFailed = new Set();
      for (const e of missing) {
        try {
          const res = await api.get('/products', { params: { search: e.sku, limit: 50 } });
          const hit = (res.data.products || []).find(p => (p.sku || '').toUpperCase() === e.sku);
          if (hit) bySku.set(e.sku, hit);
        } catch (_) { lookupFailed.add(e.sku); }
      }
      let added = 0;
      const notFound = [];
      entries.forEach(e => {
        const p = !e.error && bySku.get(e.sku);
        if (p) { addItem(p, e.qty); added += 1; }
        else notFound.push(e);
      });
      if (added > 0) toast.success(`Added ${added} SKU${added === 1 ? '' : 's'} to your cart`);
      // Remove successful lines so retrying cannot add them twice.
      setQuickText(notFound.map(e => e.raw).join('\n'));
      setQuickResult(`${added} line${added === 1 ? '' : 's'} added. ${notFound.length ? 'Only unresolved lines remain: ' + notFound.map(e => e.error ? `${e.raw}: ${e.error}` : lookupFailed.has(e.sku) ? `${e.sku}: lookup failed, retry` : `${e.sku}: not found`).join('; ') : 'All lines added.'}`);
    } finally {
      setQuickBusy(false);
      quickLock.current = false;
    }
  };

  const pickCategory = (cat) => setCategory(cat);

  const pillClass = (selected) =>
    `shrink-0 min-h-[44px] px-3.5 rounded-[var(--radius)] text-xs font-medium border transition-colors ${focusRing} ${
      selected
        ? 'bg-primary text-primary-foreground border-primary'
        : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
    }`;

  return (
    <main id="main-content" tabIndex={-1} className="min-h-[100dvh]">
      {/* Masthead: access state on the left, the account's standing on the right. */}
      <div className="border-b border-border bg-sunk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)] lg:items-end">
          <div>
            <p className="label-eyebrow">{access.eyebrow}</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl" data-testid="catalog-title">
              Find it. Price it. Build the order.
            </h1>
            <p className="mt-4 max-w-2xl text-sm sm:text-base leading-relaxed text-muted-foreground">
              {Number(total) > 0 ? `Search ${total} live products` : 'Search the live catalog'} by name or SKU. Prices are shown per listed unit or kit.
              One kit is ten vials.
            </p>
          </div>

          <div className="border-t-2 border-t-primary border-x border-b border-border bg-card p-5">
            <p className="label-quiet">Your access</p>
            <p className="mt-2 font-display text-lg">
              {access.title}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {access.description}
            </p>
            {access.showMembershipLink ? (
              <Link
                to="/register?intent=member"
                className={`mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-primary hover:underline underline-offset-4 ${focusRing}`}
              >
                Use a membership invite
                <span aria-hidden="true">&rarr;</span>
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint" aria-hidden="true" />
            <Input
              aria-label="Search products"
              placeholder="Search products by name or SKU..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-card border-border"
              data-testid="catalog-search-input"
            />
          </div>
          <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
            <SelectTrigger aria-label="Product category" className="w-full sm:w-48 bg-card border-border h-11" data-testid="catalog-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" className="h-11 w-11 sm:h-9 sm:w-9" onClick={() => setViewMode('grid')} aria-label="Grid view" aria-pressed={viewMode === 'grid'}><Grid3X3 className="h-4 w-4" /></Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" className="h-11 w-11 sm:h-9 sm:w-9" onClick={() => setViewMode('list')} aria-label="List view" aria-pressed={viewMode === 'list'}><List className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Quick category pills, sticky under navbar on mobile */}
        {/* top-14 matches the shrunk (scrolled) navbar height so the rail never floats below it */}
        <div className="sticky top-14 z-30 -mx-4 px-4 sm:mx-0 sm:px-0 py-2 mt-4 bg-[hsl(var(--background)/0.92)] backdrop-blur-sm">
          <div className="catalog-category-rail flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Quick category filters">
            <button
              type="button"
              onClick={() => pickCategory('')}
              aria-pressed={category === ''}
              className={pillClass(category === '')}
              data-testid="catalog-category-pill-all"
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => pickCategory(cat)}
                aria-pressed={category === cat}
                className={pillClass(category === cat)}
                data-testid={`catalog-category-pill-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Quick-order pad: paste SKU list for repeat wholesale buyers */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setQuickOpen(o => !o)}
            aria-expanded={quickOpen}
            className={`flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-[hsl(var(--primary-hover))] min-h-[44px] ${focusRing}`}
            data-testid="quick-order-toggle"
          >
            <Zap className="h-4 w-4" aria-hidden="true" />
            Quick order by SKU
            <ChevronDown className={`h-4 w-4 transition-transform ${quickOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {quickOpen && (
            <div className="mt-2 border border-border bg-card p-4 space-y-3 swap-in">
              <Textarea
                aria-label="Quick order SKUs"
                disabled={quickBusy}
                value={quickText}
                onChange={(e) => setQuickText(e.target.value)}
                placeholder={'Paste your list, one per line or comma-separated:\nR320 x10\nC1295 x5\nBAC30 x2'}
                className="min-h-[96px] font-mono text-sm bg-sunk border-border"
                data-testid="quick-order-input"
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">Format: SKU x quantity. Unknown SKUs are reported and skipped.</p>
                <Button size="sm" className="h-11 shrink-0" onClick={handleQuickAdd} disabled={quickBusy || !quickText.trim()} data-testid="quick-order-add">
                  {quickBusy ? 'Adding...' : 'Add all to cart'}
                </Button>
              </div>
              {quickResult && <p role="status" className="text-sm text-muted-foreground">{quickResult}</p>}
            </div>
          )}
        </div>

        {/* Results count */}
        <p className="mt-6 mb-3 label-quiet" role="status">
          {loading ? 'Searching products…' : fetchFailed ? 'Catalog unavailable' : <><span className="font-mono tabular-nums">{total}</span> products found</>}
        </p>

        {/* Products */}
        {loading ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3' : 'space-y-2'}>
            {[...Array(8)].map((_, i) => <Skeleton key={`skeleton-${i}`} className={`${viewMode === 'grid' ? 'h-[25rem]' : 'h-24'} rounded-[var(--radius)] bg-sunk`} />)}
          </div>
        ) : fetchFailed ? (
          <div className="border border-border bg-card" role="alert" data-testid="catalog-error">
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Package className="h-10 w-10 text-faint mb-4" aria-hidden="true" />
              <p className="font-display text-lg">Products could not be loaded</p>
              <p className="text-sm text-muted-foreground mt-1">Check your connection and try again.</p>
              <Button className="mt-5 h-10" onClick={fetchProducts} data-testid="catalog-retry">
                Retry
              </Button>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="border border-border bg-card">
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Package className="h-10 w-10 text-faint mb-4" aria-hidden="true" />
              <p className="font-display text-lg">No products found</p>
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filter.</p>
              <Button variant="outline" className="mt-5 min-h-11" onClick={() => { setSearch(''); setCategory(''); }}>Clear search and filters</Button>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
          >
            {products.map(product => (
              <motion.div key={product.id} variants={reduceMotion ? undefined : gridItem}
                initial={reduceMotion ? false : 'hidden'}
                whileInView={reduceMotion ? undefined : 'show'}
                viewport={{ once: true, amount: 0.05 }}
                className="h-full">
                <article
                  className="specimen-product-card group flex h-full flex-col border border-border bg-card hover:border-[hsl(var(--primary)/0.5)]"
                  data-testid="product-card"
                >
                  <div className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
                    <span className="font-mono text-[0.7rem] tracking-[0.06em] text-primary truncate">{product.sku}</span>
                    {typeof product.coa_url === 'string' && product.coa_url.trim() && (
                      <span className="inline-flex shrink-0 items-center gap-1 label-quiet" title="Certificate of analysis available">
                        <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                        COA
                      </span>
                    )}
                  </div>

                  <Link
                    to={`/products/${product.id}`}
                    state={{ catalogSearch: params.toString() }}
                    className={`flex min-w-0 flex-1 flex-col ${focusRing}`}
                  >
                    <ProductSpecimen product={product} />
                    <div className="flex flex-1 flex-col px-4 py-4 border-t border-border">
                      <h3 className="font-display text-lg leading-snug break-words transition-colors group-hover:text-primary">{product.name}</h3>
                      <p className="mt-1.5 font-mono text-[0.72rem] text-muted-foreground">{product.specification}</p>
                      <p className="mt-auto pt-4 label-quiet">{product.category}</p>
                    </div>
                  </Link>

                  <div className="flex items-end justify-between gap-3 border-t border-border bg-sunk px-3.5 py-3">
                    <span className="min-w-0">
                      <span className="block font-mono text-lg tabular-nums">${product.price.toFixed(2)}</span>
                      <span className="mt-0.5 block label-quiet">{unitLabel(product.specification)}</span>
                    </span>
                    <Button size="sm" className="h-11 shrink-0" onClick={() => handleAddToCart(product)} data-testid="product-card-add-to-cart-button">
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {isGuest ? 'Build cart' : 'Add'}
                    </Button>
                  </div>
                </article>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="border-t border-border">
            {/* Column headers read as an instrument register, hidden on small screens. */}
            <div className="hidden md:grid grid-cols-[7rem_minmax(0,1fr)_9rem_7rem_auto] items-center gap-4 border-b border-border px-3 py-2">
              <span className="label-quiet">SKU</span>
              <span className="label-quiet">Product</span>
              <span className="label-quiet">Category</span>
              <span className="label-quiet text-right">Price</span>
              <span className="sr-only">Actions</span>
            </div>
            {products.map(product => (
              <div
                key={product.id}
                className="grid grid-cols-1 md:grid-cols-[7rem_minmax(0,1fr)_9rem_7rem_auto] items-start md:items-center gap-2 md:gap-4 border-b border-border px-3 py-3 transition-colors hover:bg-sunk"
                data-testid="product-card"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[0.72rem] tracking-[0.06em] text-primary truncate">{product.sku}</span>
                  {typeof product.coa_url === 'string' && product.coa_url.trim() && <FileText className="h-3.5 w-3.5 shrink-0 text-faint" aria-label="Certificate of analysis available" />}
                </div>

                <Link to={`/products/${product.id}`} state={{ catalogSearch: params.toString() }} className={`min-w-0 ${focusRing}`}>
                  <h3 className="font-display text-base leading-snug hover:text-primary transition-colors">{product.name}</h3>
                  <p className="mt-0.5 font-mono text-[0.72rem] text-muted-foreground">{product.specification}</p>
                </Link>

                <span className="label-quiet">{product.category}</span>

                <div className="md:text-right">
                  <span className="font-mono text-sm tabular-nums">${product.price.toFixed(2)}</span>
                  <span className="ml-2 md:ml-0 md:mt-0.5 md:block label-quiet">{unitLabel(product.specification)}</span>
                </div>

                <Button size="sm" className="h-11 justify-self-start md:justify-self-end shrink-0" onClick={() => handleAddToCart(product)} data-testid="product-card-add-to-cart-button">
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" /> {isGuest ? 'Build cart' : 'Add'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function Package(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.5 9.4 7.55 4.24" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.29 7 12 12 20.71 7" /><line x1="12" x2="12" y1="22" y2="12" />
    </svg>
  );
}
